import React from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Paper,
    Grid,
    Typography,
    Toolbar,
} from '@mui/material';
import PropTypes from 'prop-types';
import NoTableDataIcon from '../../Icons/empty_table_placeholder.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import { lightGrayColor, white, Grey3 } from '../../color_utils';
import { trakrefFontFamily } from '../../utils';
import { renderWeight } from '../../Utils/weight_utils';

const TransferTable = ({
    renderEmptyPlaceholder,
    type,
    data,
    columns,
    isDeletable,
    onDataChange,
    width,
    tableHeader,
    alignCellData,
}) => {
    const showTableHeader = tableHeader !== "";
    const columnHeaderStyle = { color: Grey3, fontSize: '10px', fontFamily: trakrefFontFamily, paddingLeft: '2.2%', textTransform: 'uppercase' };
    const openSansFont = { fontFamily: trakrefFontFamily };

    const deleteEntry = (deletedRow) => {
        const { transferID, localCylinderId = '' } = deletedRow;
        const deletingUnknownCylinder = !!localCylinderId.length;
        const cylinderIdToDelete = deletingUnknownCylinder ? localCylinderId : transferID;

        // if user is deleting gas transfer from unknown cylinder, we need to use the
        // locally created cylinder ID (unique ID) assigned to each unknown cylinder when added
        // to the gas transfer table

        const newRows = data.map(x => ({
            ...x,
            enabled: (deletingUnknownCylinder ? x.localCylinderId : x.transferID) === cylinderIdToDelete ? false : x.enabled
        })).filter(x => (x.localCylinderId && x.enabled) || x.transferID);;

        return onDataChange(newRows, cylinderIdToDelete);
    };

    if (renderEmptyPlaceholder) {
        return (
            <Grid container component={Paper}>
                <Grid item xs={12} lg={12} align='center'>
                    <img
                        style={{ paddingTop: '4%' }}
                        src={NoTableDataIcon}
                        alt="No data"
                    />
                </Grid>
                <Grid item xs={12} lg={12}>
                    <Typography
                        variant='h5'
                        align='center'
                        style={{ marginTop: '2%', paddingBottom: '2%', color: '#1A173B' }}>
                        It is empty here...
                    </Typography>
                </Grid>
                <Grid item xs={12} lg={12}>
                    <Typography
                        variant='h5'
                        align='center'
                        style={{ marginTop: '2%', paddingBottom: '2%', color: '#1A173B' }}
                    >
                        {`Please click on ${type} Gas to get started`}
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <TableContainer component={Paper} sx={{ width: { xs: `100%`, lg: `${width}%` } }}>
            {showTableHeader && (
                <Toolbar>
                    <Typography
                        variant="h6"
                        component="div"
                        
                    >
                        {tableHeader}
                    </Typography>
                </Toolbar>
            )}
            <Table aria-label="simple table">
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell
                                align={alignCellData}
                                style={columnHeaderStyle}
                            >
                                {col.name || ''}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(data.filter(x => !!x.enabled) || []).map((entry, index) => (
                        <TableRow
                            key={entry.cylinderId}
                            sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                backgroundColor: index % 2 ? white : lightGrayColor,
                            }}
                        >
                            <TableCell
                                align={alignCellData}
                                component="th"
                                scope="row"
                                style={openSansFont}
                            >
                                {entry.name}
                            </TableCell>
                            <TableCell
                                align={alignCellData}
                                style={openSansFont}
                            >
                                {entry.amount ? renderWeight(entry.amount) : renderWeight(entry.transferWeightLbs)}
                            </TableCell>
                            {
                                type === "Install" && (
                                    <TableCell
                                        align={alignCellData}
                                        style={openSansFont}
                                    >
                                        {entry.materialStatusName}
                                    </TableCell>
                                )
                            }
                            {
                                type === "Recover" && (
                                    <TableCell
                                        align={alignCellData}
                                        style={openSansFont}
                                    >
                                        {entry.recoverGasStatusName}
                                    </TableCell>
                                )
                            }
                            <TableCell
                                align={alignCellData}
                                style={openSansFont}
                            >
                                {entry.cylinderNotes}
                            </TableCell>
                            <TableCell
                                align={alignCellData}
                                style={openSansFont}
                            >
                                {entry.notes}
                            </TableCell>
                            {isDeletable && (
                                <TableCell>
                                    <IconButton
                                        onClick={() => deleteEntry(entry)}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

TransferTable.propTypes = {
    data: PropTypes.object,
    columns: PropTypes.array,
    onDataChange: PropTypes.func,
    isDeletable: PropTypes.bool,
    width: PropTypes.number,
    renderEmptyPlaceholder: PropTypes.bool,
    type: PropTypes.string,
    tableHeader: PropTypes.string,
    alignCellData: PropTypes.string,
};

TransferTable.defaultProps = {
    data: [],
    columns: [],
    onDataChange: () => { },
    isDeletable: false,
    width: 100,
    renderEmptyPlaceholder: false,
    type: "Recover",
    tableHeader: "",
    alignCellData: "center",
};

export default TransferTable;
